import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  cloneInvoiceFilters,
  countActiveInvoiceFilters,
  EMPTY_INVOICE_FILTERS,
} from "@/lib/invoiceFilters";
import {
  useGetProcessingStatusesQuery,
  useGetReviewStatusesQuery,
} from "@/services/invoices/invoicesApi";
import { useGetUsersQuery } from "@/services/users/usersApi";
import {
  getProcessingStatusColor,
  getReviewStatusColor,
} from "@/lib/semanticColors";
import { PROCESSING_STATUSES, REVIEW_STATUSES } from "@/lib/statusEnums";
import { cn } from "@/lib/utils";

/**
 * Status lists come from the API so the filter always offers exactly what the
 * server recognises. The static lists in `statusEnums` are the fallback for the
 * moment before the request resolves, and keep the colours consistent either way.
 */
function useStatusOptions() {
  const { data: reviewStatuses } = useGetReviewStatusesQuery();
  const { data: processingStatuses } = useGetProcessingStatusesQuery();

  return useMemo(
    () => ({
      review: reviewStatuses?.length
        ? reviewStatuses.map((status) => ({
            id: status.id,
            label: status.name,
            color: getReviewStatusColor(status.id),
          }))
        : REVIEW_STATUSES,
      processing: processingStatuses?.length
        ? processingStatuses.map((status) => ({
            id: status.id,
            label: status.name,
            color: getProcessingStatusColor(status.id),
          }))
        : PROCESSING_STATUSES,
    }),
    [reviewStatuses, processingStatuses],
  );
}

const fieldClass =
  "num h-9 w-full min-w-0 rounded-[0.8rem] border-line bg-background shadow-none";

function toggleId(ids, id, checked) {
  if (checked) return ids.includes(id) ? ids : [...ids, id];
  return ids.filter((value) => value !== id);
}

function FilterSection({ title, hint, children, className }) {
  return (
    <section className={cn("filter-offcanvas-section", className)}>
      <div className="filter-offcanvas-section-head">
        <h3 className="filter-offcanvas-section-title">{title}</h3>
        {hint && <p className="filter-offcanvas-section-hint">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function RangeFieldGroup({ legend, startId, endId, startLabel, endLabel, startValue, endValue, onStartChange, onEndChange, inputType = "date" }) {
  return (
    <fieldset className="filter-range-group">
      <legend className="filter-offcanvas-group-label">{legend}</legend>
      <div className="filter-range-row">
        <div className="filter-range-field">
          <label htmlFor={startId} className="filter-range-label">
            {startLabel}
          </label>
          <Input
            id={startId}
            type={inputType}
            min={inputType === "number" ? 0 : undefined}
            step={inputType === "number" ? "0.01" : undefined}
            inputMode={inputType === "number" ? "decimal" : undefined}
            placeholder={inputType === "number" ? "0.00" : undefined}
            value={startValue}
            onChange={(event) => onStartChange(event.target.value)}
            className={fieldClass}
          />
        </div>
        <span className="filter-range-sep" aria-hidden="true">
          to
        </span>
        <div className="filter-range-field">
          <label htmlFor={endId} className="filter-range-label">
            {endLabel}
          </label>
          <Input
            id={endId}
            type={inputType}
            min={inputType === "number" ? 0 : undefined}
            step={inputType === "number" ? "0.01" : undefined}
            inputMode={inputType === "number" ? "decimal" : undefined}
            placeholder={inputType === "number" ? "0.00" : undefined}
            value={endValue}
            onChange={(event) => onEndChange(event.target.value)}
            className={fieldClass}
          />
        </div>
      </div>
    </fieldset>
  );
}

function StatusChecklist({ legend, statuses, selectedIds, onToggle }) {
  return (
    <fieldset className="filter-status-group">
      <legend className="filter-offcanvas-group-label">{legend}</legend>
      <div className="filter-status-grid">
        {statuses.map((status) => {
          const checked = selectedIds.includes(status.id);
          const inputId = `${legend}-${status.id}`.replace(/\s+/g, "-").toLowerCase();
          return (
            <label
              key={status.id}
              htmlFor={inputId}
              className={cn("filter-status-option", checked && "filter-status-option-active")}
            >
              <Checkbox
                id={inputId}
                checked={checked}
                onCheckedChange={(value) => onToggle(status.id, value === true)}
              />
              <span className="status-dot shrink-0" style={{ backgroundColor: status.color }} />
              <span className="min-w-0 truncate">{status.label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

export function InvoiceFilterOffcanvas({
  open,
  onOpenChange,
  value,
  onApply,
  showAssignee = true,
}) {
  const [draft, setDraft] = useState(() => cloneInvoiceFilters(value));
  const statusOptions = useStatusOptions();
  // Only load the assignee list when the panel that needs it is open.
  const { data: users = [] } = useGetUsersQuery(undefined, { skip: !open || !showAssignee });

  const [wasOpen, setWasOpen] = useState(open);

  // Seed the draft from the applied filters each time the panel opens.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(cloneInvoiceFilters(value));
  }

  const activeCount = countActiveInvoiceFilters(draft);

  const apply = (event) => {
    event.preventDefault();
    onApply(cloneInvoiceFilters(draft));
    onOpenChange(false);
  };

  const reset = () => {
    const empty = cloneInvoiceFilters(EMPTY_INVOICE_FILTERS);
    setDraft(empty);
    onApply(empty);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        overlayClassName="filter-offcanvas-veil"
        className="filter-offcanvas"
      >
        <div className="filter-offcanvas-panel">
          <form className="flex h-full min-h-0 flex-col" onSubmit={apply}>
            <SheetHeader className="filter-offcanvas-header filter-offcanvas-header-compact">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <SheetTitle className="text-base font-semibold tracking-tight">Filter</SheetTitle>
                  {activeCount > 0 && (
                    <span className="num filter-offcanvas-active-count">{activeCount} active</span>
                  )}
                </div>
                <SheetDescription className="sr-only">
                  Refine the queue without leaving the table.
                </SheetDescription>
              </div>
              <SheetClose
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="filter-offcanvas-close shadow-none"
                  />
                }
              >
                <X className="size-4" />
                <span className="sr-only">Close</span>
              </SheetClose>
            </SheetHeader>

            <div className="filter-offcanvas-body">
              <FilterSection title="Status">
                <StatusChecklist
                  legend="Review"
                  statuses={statusOptions.review}
                  selectedIds={draft.reviewStatusIds}
                  onToggle={(id, checked) =>
                    setDraft((current) => ({
                      ...current,
                      reviewStatusIds: toggleId(current.reviewStatusIds, id, checked),
                    }))
                  }
                />
                <StatusChecklist
                  legend="Processing"
                  statuses={statusOptions.processing}
                  selectedIds={draft.processingStatusIds}
                  onToggle={(id, checked) =>
                    setDraft((current) => ({
                      ...current,
                      processingStatusIds: toggleId(current.processingStatusIds, id, checked),
                    }))
                  }
                />
              </FilterSection>

              <FilterSection title="Dates">
                <RangeFieldGroup
                  legend="Invoice date"
                  startId="invoice-date-after"
                  endId="invoice-date-before"
                  startLabel="After"
                  endLabel="Before"
                  startValue={draft.invoiceDateAfter}
                  endValue={draft.invoiceDateBefore}
                  onStartChange={(next) =>
                    setDraft((current) => ({ ...current, invoiceDateAfter: next }))
                  }
                  onEndChange={(next) =>
                    setDraft((current) => ({ ...current, invoiceDateBefore: next }))
                  }
                />
              </FilterSection>

              <FilterSection title="Amount">
                <RangeFieldGroup
                  legend="Total (GBP)"
                  startId="total-min"
                  endId="total-max"
                  startLabel="Min"
                  endLabel="Max"
                  startValue={draft.totalMin}
                  endValue={draft.totalMax}
                  inputType="number"
                  onStartChange={(next) =>
                    setDraft((current) => ({ ...current, totalMin: next }))
                  }
                  onEndChange={(next) =>
                    setDraft((current) => ({ ...current, totalMax: next }))
                  }
                />
              </FilterSection>

              {showAssignee && (
                <FilterSection title="Assignment">
                  <label htmlFor="assigned-to" className="sr-only">
                    Assigned to
                  </label>
                  <select
                    id="assigned-to"
                    value={draft.assignedToId}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, assignedToId: event.target.value }))
                    }
                    className="filter-offcanvas-select"
                  >
                    <option value="">Anyone</option>
                    {users.map((user) => (
                      <option key={user.guid ?? user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </FilterSection>
              )}
            </div>

            <SheetFooter className="filter-offcanvas-footer">
              <Button
                type="button"
                variant="outline"
                className="h-9 flex-1 rounded-[0.8rem] border-line shadow-none"
                onClick={reset}
              >
                Reset filters
              </Button>
              <Button type="submit" variant="default" className="h-9 flex-1 rounded-[0.8rem]">
                Apply filters
              </Button>
            </SheetFooter>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
